import type {
  DscApplicantSection,
  DscWorkbenchDashboardResponse,
  OpenLoanCheckApplicantRequest,
  OpenLoanCheckRequest,
  OpenLoanCheckResponse,
} from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { openLoanCheckApi } from "../../../api/client";
import { useDscWorkbench } from "../../review/api/useDscWorkbench";
import { openLoanCheckKeys } from "./openLoanCheckKeys";

/**
 * Maps one applicant's cached residence data to the open-loan check request
 * shape. Returns undefined when the applicant lacks a date of birth, the one
 * field the BFF requires that isn't optional.
 * @param {DscApplicantSection | undefined} applicant - The driver or second-signer dashboard section.
 * @returns {OpenLoanCheckApplicantRequest | undefined} The mapped applicant request, or undefined.
 */
function buildApplicantRequest(applicant: DscApplicantSection | undefined): OpenLoanCheckApplicantRequest | undefined {
  const residence = applicant?.residence;
  if (!residence?.dateOfBirth) {
    return undefined;
  }
  return {
    id: applicant?.applicantId,
    ssnId: residence.ssnId === undefined ? undefined : String(residence.ssnId),
    firstName: residence.firstName,
    middleName: residence.middleName,
    lastName: residence.lastName,
    dateOfBirth: residence.dateOfBirth,
    // isSsn is true for a Social Security Number, false for an ITIN.
    isNotItin: residence.isSsn ?? true,
  };
}

/**
 * Builds the open-loan check request from the cached dashboard data. Returns
 * undefined when the dashboard hasn't loaded yet, the driver lacks a date of
 * birth, or `dealerId` is missing. `dealerId` must never be omitted from the
 * request: production calls forward the analyst's own JWT, and cast-facade's
 * access check rejects requests with both `carLotId` and `dealerId` null.
 * @param {string} approvalId - The deal's approval id, which doubles as the credit application id.
 * @param {DscWorkbenchDashboardResponse | undefined} dashboard - The cached workbench dashboard.
 * @returns {OpenLoanCheckRequest | undefined} The mapped request, or undefined when required data is missing.
 */
export function buildOpenLoanCheckRequest(
  approvalId: string,
  dashboard: DscWorkbenchDashboardResponse | undefined
): OpenLoanCheckRequest | undefined {
  const dealerId = dashboard?.dealSummary?.dealerId;
  const receiptDate = dashboard?.dealSummary?.receiptDate;
  const driver = buildApplicantRequest(dashboard?.driver);
  if (!dealerId || !receiptDate || !driver) {
    return undefined;
  }
  const creditApplicationId = Number.parseInt(approvalId, 10);
  const cosigner = dashboard?.secondSigner?.present ? buildApplicantRequest(dashboard.secondSigner) : undefined;
  return {
    driver,
    cosigner,
    dealerId,
    creditApplicationId: Number.isNaN(creditApplicationId) ? undefined : creditApplicationId,
    creditApplicationReceiptDate: receiptDate,
  };
}

/**
 * `queryOptions` builder for the open-loan check. `enabled` is driven by the
 * modal's own open state (set true on the row button's click), not by the
 * Dialog's actual visibility — see `OpenLoanCheckModal`.
 * @param {string} approvalId - The deal's approval id.
 * @param {OpenLoanCheckRequest | undefined} request - The mapped request, or undefined when required data is missing.
 * @param {boolean} enabled - Whether the caller wants the check to fire (e.g. the row's modal-open flag).
 * @returns {ReturnType<typeof queryOptions<OpenLoanCheckResponse>>} The TanStack `queryOptions` for the check.
 */
export function openLoanCheckQuery(approvalId: string, request: OpenLoanCheckRequest | undefined, enabled: boolean) {
  return queryOptions({
    queryKey: openLoanCheckKeys.check(approvalId, request),
    queryFn: async ({ signal }): Promise<OpenLoanCheckResponse> => {
      if (!request) {
        throw new Error("openLoanCheckQuery: queryFn ran without a request; check `enabled`.");
      }
      const response = await openLoanCheckApi.checkOpenLoans(request, { signal });
      return response.data;
    },
    staleTime: 30_000,
    enabled: enabled && !!approvalId && !!request,
  });
}

/**
 * Fetches the open-loan check, reusing the `useDscWorkbench` cache to build
 * the request from the driver/second-signer/deal data already loaded for
 * this approval.
 * @param {string} approvalId - The deal's approval id.
 * @param {{ enabled: boolean }} options - Options bag.
 * @param {boolean} options.enabled - Gates the check (e.g. the row's modal-open flag).
 * @returns {ReturnType<typeof useQuery<OpenLoanCheckResponse>>} The TanStack `useQuery` result for the check.
 */
export function useOpenLoanCheck(approvalId: string, options: { enabled: boolean }) {
  const { data: dashboard } = useDscWorkbench(approvalId);
  const request = buildOpenLoanCheckRequest(approvalId, dashboard);
  return useQuery(openLoanCheckQuery(approvalId, request, options.enabled));
}
